import { getAppConfig } from '@services/config';

import { HttpLoggerData, LogHttpTransporterInterface, LogTransportOptions } from '@app-types/logger';

import { isServer } from '@helpers/config';

import { HttpStatus } from '@constants/http';
import { PRISME_ST } from '@constants/prisme';

import { LoggerService, logger } from './logger.service';

type LogPrisme = {
    st: string;
    requestUri: string;
    httpMethod: string;
    httpStatus: number;
    Location?: string;
    urlPattern: string;
    duree?: number;
    output?: HttpLoggerData['output'];
    message?: string;
    erreur?: HttpLoggerData['erreur'];
    input?: string;
    headers?: object;
    level?: 'LOG' | 'INFO' | 'WARN' | 'ERROR';
    appelant: 'server' | 'front';
};

type HttpErrorTexts = {
    message?: string;
    description?: string;
};

const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isOptionalText = (value: unknown): value is string | undefined =>
    value === undefined || typeof value === 'string';

abstract class LogHttpTransporterAbstract implements LogHttpTransporterInterface {
    protected logCached = false;
    public constructor(options: LogTransportOptions) {
        this.logCached = options.logCached ?? false;
    }
    abstract send(data: HttpLoggerData): void;
}

class HttpVoidTransporter extends LogHttpTransporterAbstract {
    public send(_: HttpLoggerData): void {
        // nothing
    }
}

class HttpConsoleTransporter extends LogHttpTransporterAbstract {
    logger: LoggerService;
    public constructor(options: LogTransportOptions) {
        super(options);
        this.logger = new LoggerService();
    }

    public send(data: HttpLoggerData) {
        if (data.cached) {
            this.logger.log('✅ HTTP Success', data);
        } else if (data.status >= HttpStatus.BAD_REQUEST) {
            this.logger.error('HTTP Error', data);
        } else {
            this.logger.info('HTTP Request', data);
        }
    }
}

export class HttpPrismeTransporter extends LogHttpTransporterAbstract {
    public send(data: HttpLoggerData) {
        if (!this.logCached && data.cached) {
            return;
        }

        const log = this.mapToLogFormatPrisme(data);
        if (data.status >= HttpStatus.BAD_REQUEST && data.status < 600) {
            this.normalizeHttpError(log);
        }
        this.displayLog(log);
    }

    private mapToLogFormatPrisme(data: HttpLoggerData): LogPrisme {
        const url = new URL(data.uri);
        const urlPattern = `${url.protocol}//${url.host}${url.pathname}`;
        return {
            st: PRISME_ST,
            requestUri: data.uri,
            urlPattern: urlPattern,
            httpMethod: data.method.toUpperCase(),
            httpStatus: data.status,
            headers: data.headers,
            duree: data.duration,
            input: data.input,
            output: data.output,
            message: data.message,
            erreur: data.erreur,
            level: data.status >= HttpStatus.BAD_REQUEST ? 'ERROR' : 'INFO',
            appelant: 'server',
        };
    }

    private normalizeHttpError(log: LogPrisme): void {
        if (hasText(log.message) && hasText(log.erreur?.description)) {
            return;
        }

        const texts = this.extractErrorTexts(log.output);
        if (!texts) {
            return;
        }

        if (!hasText(log.message) && hasText(texts.message)) {
            log.message = texts.message;
        }
        if (!hasText(log.erreur?.description)) {
            const description = hasText(texts.description) ? texts.description : texts.message;
            log.erreur = { ...log.erreur, description };
        }
    }

    private extractErrorTexts(rawOutput: HttpLoggerData['output']): HttpErrorTexts | null {
        const output = this.parseErrorOutput(rawOutput);
        if (!output) {
            return null;
        }

        const { message, description } = output;
        if (!(isOptionalText(message) && isOptionalText(description))) {
            logger.error('HTTP error normalization failed: message and description must be text');
            return null;
        }
        if (!(hasText(message) || hasText(description))) {
            logger.error('HTTP error normalization failed: output has no usable text');
            return null;
        }

        return { message, description };
    }

    private parseErrorOutput(rawOutput: HttpLoggerData['output']): Record<string, unknown> | null {
        let output: unknown = rawOutput;
        if (typeof output === 'string') {
            try {
                output = JSON.parse(output);
            } catch {
                logger.error('HTTP error normalization failed: output is not valid JSON');
                return null;
            }
        }
        if (!output || typeof output !== 'object' || Array.isArray(output)) {
            logger.error('HTTP error normalization failed: output must be an object');
            return null;
        }

        return output as Record<string, unknown>;
    }

    private displayLog(data: LogPrisme): void {
        console.info(JSON.stringify(data));
    }
}

const transporterFactory = (): LogHttpTransporterInterface => {
    // log only server side prisme or standard console.log
    const appConfig = getAppConfig();
    const logOption: LogTransportOptions = {
        logCached: appConfig.logger.http.logCached,
    };

    if (isServer()) {
        return appConfig.prisme.active ? new HttpPrismeTransporter(logOption) : new HttpConsoleTransporter(logOption);
    }

    return new HttpVoidTransporter(logOption);
};

export const transporter = transporterFactory();
